import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScreenInit } from '../useScreenInit.js';
import { trackPixel } from '../lib/metaPixel';
import { OFFER_URL } from '../lib/clickMagick';
import { CheckCircle2, PlayCircle, ChevronRight, Activity, ShieldCheck, FileSearch } from 'lucide-react';
type Step = 'landing' | 'quiz' | 'loading' | 'results' | 'vsl';
const questions = [{
  id: 1,
  question: 'Where do you feel joint pain the most intensely and frequently?',
  options: ['Knees', 'Hips', 'Hands & Wrists', 'Back / Shoulders', 'Multiple areas']
}, {
  id: 2,
  question: 'How does this pain affect your daily life?',
  options: ['I struggle to go up or down stairs', "I can't enjoy my favorite hobbies anymore", 'It wakes me up at night', 'I feel stiff when I get up in the morning']
}, {
  id: 3,
  question: 'What have you tried before to relieve the pain without lasting results?',
  options: ['Painkillers or anti-inflammatories (Ibuprofen, etc.)', 'Glucosamine or Chondroitin supplements', 'Topical creams or gels', 'Injections or expensive therapies']
}, {
  id: 4,
  question: "Did you know recent Harvard studies suggest the real culprit behind the pain isn't cartilage wear, but the loss of 'joint jelly'?",
  options: ["Yes, I'd heard something about it", 'No, tell me more about this discovery']
}];
export function QuizFlow() {
  const screenInit = useScreenInit() as {
    step?: Step;
    currentQuestionIndex?: number;
  };
  const [step, setStep] = useState<Step>(screenInit.step ?? 'landing');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(screenInit.currentQuestionIndex ?? 0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const loadingTexts = ['Analyzing your answers...', 'Checking your joint symptoms...', 'Cross-referencing relevant clinical studies...', 'Generating your personalized report...'];
  const progress = step === 'landing' ? 0 : step === 'quiz' ? currentQuestionIndex / questions.length * 100 : 100;
  const handleStart = () => {
    trackPixel('Lead', {
      content_name: 'Joint Quiz Started'
    });
    setStep('quiz');
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleAnswer = (answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answer
    }));
    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 300);
    } else {
      trackPixel('CompleteRegistration', {
        content_name: 'Joint Quiz Completed'
      });
      setTimeout(() => {
        setStep('loading');
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 300);
    }
  };
  useEffect(() => {
    if (step === 'loading') {
      const interval = setInterval(() => {
        setLoadingTextIndex(prev => {
          if (prev < loadingTexts.length - 1) return prev + 1;
          clearInterval(interval);
          setTimeout(() => setStep('results'), 1000);
          return prev;
        });
      }, 1500);
      return () => clearInterval(interval);
    }
  }, [step]);
  const handleShowVSL = () => {
    trackPixel('ViewContent', {
      content_name: 'VSL Presentation'
    });
    window.location.href = OFFER_URL;
  };
  return <div className="min-h-screen flex flex-col items-center w-full pb-20" data-id="element-2" data-mp="3schs2l5hgex1">
      {/* Header */}
      <header className="w-full bg-white border-b border-[#E5E2DC] sticky top-0 z-50" data-id="element-3" data-mp="a6d6ggfqeo7i">
        <div className="max-w-3xl mx-auto px-4 py-4 flex flex-col items-center justify-center" data-id="element-4" data-mp="64roigocbjqg">
          <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E35] tracking-tight" data-id="element-5" data-mp="1znh2q0clq38u">
            Dr. Weis{' '}
            <span className="font-light italic text-[#7C9082]" data-id="element-6" data-mp="24kh9yel6tc1j">
              Joint Research
            </span>
          </h1>
        </div>
        {/* Progress Bar */}
        <div className="w-full bg-[#F4F1EA] h-1.5" data-id="element-7" data-mp="2ec3ig3654x0l">
          <motion.div className="h-full bg-[#7C9082]" initial={{
          width: 0
        }} animate={{
          width: `${progress}%`
        }} transition={{
          duration: 0.5,
          ease: 'easeInOut'
        }} data-id="element-8" data-mp="2easbxvlb9b5s" />
        </div>
      </header>

      <main className="w-full max-w-2xl mx-auto px-4 mt-8 md:mt-12" data-id="element-9" data-mp="1uo464nvxhht2">
        <AnimatePresence mode="wait" data-id="element-10" data-mp="1ul9muzoni318">
          {/* LANDING STEP */}
          {step === 'landing' && <motion.div key="landing" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="text-center space-y-8" data-id="element-11" data-mp="362ri1as61uc5">
              <div className="inline-block bg-[#F4F1EA] text-[#5A6D60] px-4 py-1.5 rounded-full text-sm font-medium tracking-wide mb-4" data-id="element-12" data-mp="lkri0j5tygle">
                Attention Men & Women Over 50
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E35] leading-tight" data-id="element-13" data-mp="bmk5ccikqkp7">
                Harvard Study Reveals: Joint Pain After 50 Is{' '}
                <span className="text-[#7C9082]" data-id="element-14" data-mp="2p1gex1t7m81">Not "Wear and Tear"</span>
              </h2>

              <p className="text-lg md:text-xl text-[#4A5D50] leading-relaxed max-w-xl mx-auto" data-id="element-15" data-mp="3tvunghjaafkh">
                Take this 30-second test to discover what's really causing your
                joint pain — and how restoring your "joint jelly" could be the
                key to getting your mobility back.
              </p>

              <div className="pt-8" data-id="element-16" data-mp="2b5oswttp8vkp">
                <button onClick={handleStart} className="w-full md:w-auto bg-[#7C9082] hover:bg-[#6A7E70] text-white text-lg font-medium py-4 px-12 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center mx-auto group" data-id="element-17" data-mp="2fz1gd0mn6wbv">
                  Start My Free Test
                  <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-18" data-mp="2uwabm5f2gk8v" />
                </button>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-6 gap-y-2 text-sm text-[#8A9A8F]" data-id="element-19" data-mp="2yv1c8miqqk0q">
                  <span className="flex items-center gap-2" data-id="element-20" data-mp="2pw62x1kegtt7">
                    <Activity className="w-4 h-4" data-id="element-21" data-mp="2pys8ivnqcz8s" /> Takes just 30 seconds
                  </span>
                  <span className="flex items-center gap-2" data-id="element-22" data-mp="19kik61jhnahy">
                    <CheckCircle2 className="w-4 h-4 text-[#7C9082]" data-id="element-23" data-mp="1eibsb04cqekv" /> Over
                    47,000 people have taken it
                  </span>
                </div>
              </div>
            </motion.div>}

          {/* QUIZ STEP */}
          {step === 'quiz' && <motion.div key="quiz" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="w-full" data-id="element-24" data-mp="1fyxvng51uixy">
              <div className="mb-8 text-center" data-id="element-25" data-mp="wzk0wvzhx46e">
                <span className="text-sm font-medium text-[#7C9082] tracking-widest uppercase" data-id="element-26" data-mp="x1en0dmi72c2">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </span>
                <h3 className="text-2xl md:text-3xl font-semibold text-[#2C3E35] mt-4 leading-snug" data-id="element-27" data-mp="4i7wui2ygted">
                  {questions[currentQuestionIndex].question}
                </h3>
              </div>

              <div className="space-y-3" data-id="element-28" data-mp="3b5l4pw1azcxv">
                {questions[currentQuestionIndex].options.map((option, idx) => <button key={idx} onClick={() => handleAnswer(option)} className="w-full text-left p-5 rounded-xl border-2 border-[#E5E2DC] bg-white hover:border-[#7C9082] hover:bg-[#F4F1EA] transition-all text-[#2C3E35] text-lg font-medium shadow-sm hover:shadow-md flex items-center justify-between group" data-id="element-29" data-mp="3f7ol8z2tx7q0" data-mpi={idx}>
                    <span data-id="element-30" data-mp="1wz8t9054oqk">{option}</span>
                    <div className="w-6 h-6 rounded-full border-2 border-[#D1CEC7] group-hover:border-[#7C9082] flex items-center justify-center" data-id="element-31" data-mp="6pa9wf6magkp">
                      <div className="w-3 h-3 rounded-full bg-[#7C9082] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-32" data-mp="bo3n06hzs5kc" />
                    </div>
                  </button>)}
              </div>
            </motion.div>}

          {/* LOADING STEP */}
          {step === 'loading' && <motion.div key="loading" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="flex flex-col items-center justify-center py-20 text-center space-y-8" data-id="element-33" data-mp="262qzgvv4e7g9">
              <div className="relative w-24 h-24" data-id="element-34" data-mp="2ov1ygemnll93">
                <motion.div className="absolute inset-0 border-4 border-[#E5E2DC] rounded-full" data-id="element-35" data-mp="2jws2w2x0oimy" />
                <motion.div className="absolute inset-0 border-4 border-[#7C9082] rounded-full border-t-transparent" animate={{
              rotate: 360
            }} transition={{
              duration: 1,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-36" data-mp="2f5ie14jaljc4" />
                <div className="absolute inset-0 flex items-center justify-center text-[#7C9082]" data-id="element-37" data-mp="kqwgiiye77qn">
                  <FileSearch className="w-8 h-8" data-id="element-38" data-mp="gpugw10kcto5" />
                </div>
              </div>

              <div className="space-y-4" data-id="element-39" data-mp="upc62hhru8y1">
                <h3 className="text-2xl font-serif text-[#2C3E35]" data-id="element-40" data-mp="uq58555sca9u">
                  Analyzing Your Profile...
                </h3>
                <div className="h-6 overflow-hidden" data-id="element-41" data-mp="psc3igk4robo">
                  <AnimatePresence mode="wait" data-id="element-42" data-mp="13oz0gjc32ehl">
                    <motion.p key={loadingTextIndex} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} className="text-[#5A6D60] font-medium" data-id="element-43" data-mp="13nnvig6wj0ow">
                      {loadingTexts[loadingTextIndex]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>}

          {/* RESULTS STEP */}
          {step === 'results' && <motion.div key="results" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white rounded-2xl shadow-xl p-8 md:p-10 text-center border border-[#E5E2DC]" data-id="element-44" data-mp="1s4n1f3mxkcp0">
              <div className="w-16 h-16 bg-[#F4F1EA] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-45" data-mp="19dc7septlltf">
                <ShieldCheck className="w-8 h-8 text-[#7C9082]" data-id="element-46" data-mp="1ebdt76apxx82" />
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-[#2C3E35] mb-4" data-id="element-47" data-mp="zf6c4eulxbx0">
                There's hope! Your pain is not a life sentence.
              </h2>

              <div className="space-y-6 text-left mt-8" data-id="element-48" data-mp="l8uxanmo5til">
                <p className="text-lg text-[#4A5D50] leading-relaxed" data-id="element-49" data-mp="lafr2b15t8fe">
                  Your answers suggest your cartilage may be suffering from{' '}
                  <strong data-id="element-50" data-mp="bhqdnam1tkhf">joint dehydration</strong>, not permanent,
                  irreversible wear and tear.
                </p>
                <div className="bg-[#F4F1EA] p-6 rounded-xl border-l-4 border-[#7C9082]" data-id="element-51" data-mp="6l9terhyzq9m">
                  <p className="text-[#2C3E35] font-medium" data-id="element-52" data-mp="146p18n8xxeu4">
                    The real problem is usually the loss of synovial fluid (the
                    "joint jelly"). When this jelly dries out, the bones rub
                    together, causing inflammation and sharp pain.
                  </p>
                </div>
                <p className="text-lg text-[#4A5D50] leading-relaxed" data-id="element-53" data-mp="pcao33c24znd">
                  Fortunately, a recent discovery has revealed a simple method
                  to rehydrate your joints naturally — no matter your age.
                </p>
              </div>

              <div className="mt-10" data-id="element-54" data-mp="1pjpkxr3051fy">
                <button onClick={handleShowVSL} className="w-full bg-[#C2754E] hover:bg-[#A9623F] text-white text-xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-1 flex flex-col items-center justify-center gap-2" data-id="element-55" data-mp="1uh7k4pr7w2ar">
                  <span data-id="element-56" data-mp="296n9c1b3nzoc">Watch Dr. Weis's video presentation now</span>
                  <span className="text-sm font-normal opacity-90" data-id="element-57" data-mp="298gefc62ebu8">
                    Click here to watch the free video
                  </span>
                </button>
              </div>
            </motion.div>}

          {/* VSL PLACEHOLDER STEP */}
          {step === 'vsl' && <motion.div key="vsl" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-8" data-id="element-58" data-mp="2n3iv8ucg9llt">
              <div className="text-center space-y-4" data-id="element-59" data-mp="2jb2cxtarp2sd">
                <h2 className="text-3xl md:text-4xl font-bold text-[#2C3E35] leading-tight" data-id="element-60" data-mp="2eao5acd2vuuv">
                  Discover the 10-Second Method to Restore Your "Joint Jelly"
                </h2>
                <p className="text-lg text-[#5A6D60]" data-id="element-61" data-mp="2o7sqo9z3nmam">
                  Make sure your sound is turned on. The presentation will begin
                  shortly.
                </p>
              </div>

              {/* Video Placeholder */}
              <div className="w-full aspect-video bg-black rounded-xl shadow-2xl relative overflow-hidden group cursor-pointer border-4 border-[#2C3E35]" data-id="element-62" data-mp="1vl7yj7asv660">
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-63" data-mp="24jtecuow0733">
                  <div className="bg-white/10 p-4 rounded-full backdrop-blur-sm group-hover:bg-white/20 transition-all" data-id="element-64" data-mp="1gu8rw1tiyrp2">
                    <PlayCircle className="w-16 h-16 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" data-id="element-65" data-mp="1crucjrp2ea2j" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent" data-id="element-66" data-mp="17g73drqm6bdg">
                  <div className="w-full bg-white/30 h-1.5 rounded-full overflow-hidden" data-id="element-67" data-mp="sru1yf8rc0bk">
                    <div className="w-1/3 h-full bg-[#7C9082]" data-id="element-68" data-mp="xp2pywgl6ykq" />
                  </div>
                  <div className="flex justify-between text-white/80 text-sm mt-2" data-id="element-69" data-mp="1lex199l9cf2v">
                    <span data-id="element-70" data-mp="1qe9ldmkqv81r">00:00</span>
                    <span data-id="element-71" data-mp="1qd895erzsfig">24:15</span>
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-72" data-mp="1bi22cpl2oco2">
                  <p className="text-white/50 font-mono text-sm" data-id="element-73" data-mp="1gfkawev6gpvj">
                    [ VSL PLACEHOLDER ]
                  </p>
                </div>
              </div>

              <div className="text-center mt-8" data-id="element-74" data-mp="516q5ivq0qjq">
                <p className="text-sm text-[#8A9A8F]" data-id="element-75" data-mp="52roljl5otmv">
                  © {new Date().getFullYear()} Dr. Weis Joint Research. All
                  rights reserved.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </main>
    </div>;
}